<template>
    <div
        class="GalleryHero"
        :class="{
            'GalleryHero--is-paused': isPaused,
            'GalleryHero--ready': !loadingScreenActive,
        }"
        :style="'--progress:' + progress + '%'"
    >
        <Splide
            :options="{
                arrows: false,
                type: 'loop',
                autoplay: true,
                pauseOnHover: false,
                perPage: 1,
                classes: {
                    pagination: 'splide__pagination GalleryHeroPagination',
                },
            }"
            @splide:pagination:mounted="buildPagination"
            @splide:autoplay:playing="handlePlaybackRate"
            @splide:dragged="handleDragged"
            @splide:moved="handleMove"
            @splide:pagination:updated="handlePaginationUpdated"
            class="GalleryHero__splide"
        >
            <template v-for="(item, index) in items">
                <GalleryHeroSlide
                    :item="item"
                    :index="index"
                    :remove-force-in-for-slide="removeForceInForSlide"
                />
            </template>
        </Splide>
        <IconButton
            icon="arrow"
            :rotate="135"
            filled="none"
            class="GalleryHero__scroll-down"
            @click="scrollTo"
        />
    </div>
</template>

<script setup lang="ts">
import { Splide } from '@splidejs/vue-splide'
import gsap from 'gsap'
import '@splidejs/vue-splide/css/core'

interface PrismicImage {
    url: string
    dimensions: {
        width: number
        height: number
    }
}

interface GalleryHeroItem {
    image: PrismicImage
    title: string
    logo: PrismicImage
    tags: Array<string>
    galleryShortCutText: string
    to: string
    href: string
}

interface GalleryHeroProps {
    items: Array<GalleryHeroItem>
}

const props = defineProps<GalleryHeroProps>()

const { hook } = useNuxtApp()
const progress = ref(0)
const isPaused = ref(false)
const removeForceInForSlide = ref(false)
const loadingScreenActive = ref(true)
const activeSlideIndex = ref(0)

hook('page:loading:end', () => {
    setTimeout(() => {
        loadingScreenActive.value = false
    }, 750)
})

function buildPagination(splide: any, data: any) {
    data.items.forEach((item: any) => {
        if (item.page === 0) {
            item.li.classList.add('is-active')
        }

        item.button.textContent = String(
            props.items[item.page].galleryShortCutText,
        )
    })
}

function handlePlaybackRate(splide: any, rate: number) {
    progress.value = Math.round(rate * 100)
}

function handleDragged(splide: any) {
    splide.Components.Autoplay.pause()
    isPaused.value = true
}

function handleMove(newIndex: number, prevIndex: number, destIndex: number) {
    activeSlideIndex.value = newIndex
    removeForceInForSlide.value = true
}

function handlePaginationUpdated(splide: any, data: any, prev: any, curr: any) {
    nextTick(() => {
        curr.li.classList.add('is-active')
        prev.li.classList.remove('is-active')
    })
}

function scrollTo() {
    gsap.to(window, { duration: 0.5, scrollTo: { y: '#projects' } })
}
</script>

<style lang="scss">
.GalleryHero {
    height: 100vh;
    height: 100svh;

    .GalleryHeroPagination {
        position: absolute;
        bottom: 3.25rem;
        right: 10%;
        display: flex;
        width: 500px;
        flex-wrap: nowrap;
        align-items: flex-start;
        gap: 16px;
        opacity: 0;
        transition: opacity 1s ease;

        @include small-only {
            right: 4%;
            left: 4%;
            width: 70%;
        }

        @include medium-only {
            right: 15%;
        }

        li {
            width: 100%;
            line-height: 0;

            @include small-only {
                display: none;
                flex-shrink: 0;

                &:has(.is-active) {
                    display: inline-block;
                    width: 100%;
                }
            }
        }
    }

    .splide__pagination__page {
        @include heading-6;

        font-size: var(--font-size-body-sm);
        position: relative;
        width: 100%;
        color: var(--white);
        text-align: left;
        padding-top: 8px;
        padding-bottom: 8px;

        @include small-only {
            display: none;
        }

        &::before {
            content: '';
            position: absolute;
            width: 100%;
            height: 2px;
            top: 0;
            background-color: var(--white);
            opacity: 0.5;
        }

        &::after {
            content: '';
            position: absolute;
            width: 0;
            height: 2px;
            top: 0;
            left: 0;
            background-color: var(--white);
        }

        &.is-active {
            @include small-only {
                display: block;
            }

            &::after {
                width: var(--progress);
            }
        }
    }
}

.GalleryHero--is-paused {
    .splide__pagination__page.is-active::after {
        width: 100%;
    }
}

.GalleryHero--ready {
    .GalleryHero__scroll-down,
    .splide__pagination {
        opacity: 1;
    }
}

.GalleryHero__scroll-down {
    position: absolute;
    bottom: 3.5rem;
    right: 3%;
    opacity: 0;
    transition: opacity 1s ease;
}

.GalleryHero__splide,
.GalleryHero__splide-track,
.GalleryHero .splide__track {
    height: 100%;
}
</style>
